import type {
  AdminSession,
  AppSettings,
  CreateShareResponse,
  FileRecord,
  PublicShareMetadata,
  RegeneratePasskeyResponse,
  ShareTheme,
  UnlockShareResponse,
  UploadListResponse
} from "@xiaotian-vault/shared";

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

export type ApiClient = {
  csrfToken: string | null;
  me: () => Promise<AdminSession>;
  login: (input: { username: string; password: string; totpCode: string }) => Promise<AdminSession>;
  logout: () => Promise<void>;
  getSettings: () => Promise<AppSettings>;
  updateSettings: (input: { publicShareTheme: ShareTheme }) => Promise<AppSettings>;
  listFiles: () => Promise<FileRecord[]>;
  createShare: (fileId: string, expiresAt: string | null) => Promise<CreateShareResponse>;
  updateShare: (shareId: string, input: { enabled?: boolean; expiresAt?: string | null }) => Promise<void>;
  regeneratePasskey: (shareId: string) => Promise<RegeneratePasskeyResponse>;
  deleteFile: (fileId: string) => Promise<void>;
  publicMetadata: (token: string) => Promise<PublicShareMetadata>;
  unlockShare: (token: string, passkey: string) => Promise<UnlockShareResponse>;
};

export function createApiClient(): ApiClient {
  let csrfToken: string | null = null;

  async function request<T>(path: string, options: RequestInit = {}, csrf = false): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    if (csrf && csrfToken) headers.set("x-csrf-token", csrfToken);

    const response = await fetch(path, {
      ...options,
      headers,
      credentials: "include"
    });

    const text = await response.text();
    const payload = text ? JSON.parse(text) : null;
    if (!response.ok) {
      throw new ApiClientError(payload?.error?.code ?? "request_failed", payload?.error?.message ?? "Request failed.", response.status);
    }
    return payload as T;
  }

  return {
    get csrfToken() {
      return csrfToken;
    },
    async me() {
      const session = await request<AdminSession>("/api/auth/me");
      csrfToken = session.csrfToken;
      return session;
    },
    async login(input) {
      const session = await request<AdminSession>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(input)
      });
      csrfToken = session.csrfToken;
      return session;
    },
    async logout() {
      await request("/api/auth/logout", { method: "POST" }, true);
      csrfToken = null;
    },
    getSettings() {
      return request<AppSettings>("/api/settings");
    },
    updateSettings(input) {
      return request<AppSettings>(
        "/api/settings",
        { method: "PATCH", body: JSON.stringify(input) },
        true
      );
    },
    async listFiles() {
      return (await request<UploadListResponse>("/api/files")).files;
    },
    createShare(fileId, expiresAt) {
      return request<CreateShareResponse>(
        `/api/files/${encodeURIComponent(fileId)}/shares`,
        { method: "POST", body: JSON.stringify({ expiresAt }) },
        true
      );
    },
    async updateShare(shareId, input) {
      await request(`/api/shares/${encodeURIComponent(shareId)}`, { method: "PATCH", body: JSON.stringify(input) }, true);
    },
    regeneratePasskey(shareId) {
      return request<RegeneratePasskeyResponse>(`/api/shares/${encodeURIComponent(shareId)}/regenerate-passkey`, { method: "POST" }, true);
    },
    async deleteFile(fileId) {
      await request(`/api/files/${encodeURIComponent(fileId)}`, { method: "DELETE" }, true);
    },
    publicMetadata(token) {
      return request<PublicShareMetadata>(`/api/public/shares/${encodeURIComponent(token)}`);
    },
    unlockShare(token, passkey) {
      return request<UnlockShareResponse>(`/api/public/shares/${encodeURIComponent(token)}/unlock`, {
        method: "POST",
        body: JSON.stringify({ passkey })
      });
    }
  };
}
